/** One change to a requested part. Which side is empty says what it is:
 *  rename — both set ("Door" → "Door handle"); remove — value empty;
 *  add — original empty. The backend reads the same rule
 *  (chat_new.part_edit_operations). */
export interface PartEdit {
  original: string;
  value: string;
}

export type PartEditKind = 'rename' | 'remove' | 'add';

export const partEditKind = (edit: PartEdit): PartEditKind =>
  edit.original && edit.value ? 'rename' : edit.original ? 'remove' : 'add';

/** Build one hidden command so multi-part edits are processed atomically. */
export function buildPartEditMarker(edits: PartEdit[]): string {
  const changed = edits
    .map(({ original, value }) => ({ original: original.trim(), value: value.trim() }))
    .filter(({ original, value }) => (original || value) && original !== value);

  return `SYSTEM_MESSAGE:PART_EDIT|payload=${encodeURIComponent(JSON.stringify(changed))}`;
}

const PERSISTED_PART_EDIT_PREFIX =
  'Edit only these previously searched part requests and re-search only the edited requests';

/**
 * Part edits are sent as hidden commands, but older persisted sessions can contain
 * the server's plain-language rewrite of that command. Neither representation is
 * user-authored chat content, so history renders a compact change indicator instead.
 */
export function isPersistedPartEditInstruction(value?: string | null): boolean {
  const text = (value || '').trim();
  return text.startsWith('SYSTEM_MESSAGE:PART_EDIT|')
    || text.startsWith(PERSISTED_PART_EDIT_PREFIX);
}

/** Recover edit details from either the live marker or its persisted rewrite. */
export function parsePersistedPartEdits(value?: string | null): PartEdit[] {
  const text = (value || '').trim();
  if (text.startsWith('SYSTEM_MESSAGE:PART_EDIT|')) {
    const payload = text.match(/(?:^|\|)payload=([^|]+)/)?.[1];
    if (!payload) return [];
    try {
      const parsed = JSON.parse(decodeURIComponent(payload));
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map(item => ({
          original: String(item?.original || '').trim(),
          value: String(item?.value || '').trim(),
        }))
        .filter(item => (item.original || item.value) && item.original !== item.value);
    } catch {
      return [];
    }
  }

  if (!text.startsWith(PERSISTED_PART_EDIT_PREFIX)) return [];
  const edits: PartEdit[] = [];
  const pattern = /change "([^"]+)" to "([^"]+)"|remove "([^"]+)"|add "([^"]+)"/g;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    if (match[1] !== undefined) edits.push({ original: match[1].trim(), value: match[2].trim() });
    else if (match[3] !== undefined) edits.push({ original: match[3].trim(), value: '' });
    else edits.push({ original: '', value: match[4].trim() });
  }
  return edits;
}

const normalized = (value?: string): string => (value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, ' ')
  .trim()
  .split(/\s+/)
  .filter(Boolean)
  .filter((token, index, all) => all.indexOf(token) === index)
  .sort()
  .join(' ');

/**
 * Remove the previous row when a cumulative validation response contains both
 * the old `previous` item and its new `edited` replacement.
 */
export function removeSupersededPartRows<T extends {
  name: string;
  displayLabel?: string;
  status: string;
  oldName?: string;
}>(items: T[]): T[] {
  const superseded = new Set(
    items
      .filter(item => item.status === 'edited' && item.oldName)
      .map(item => normalized(item.oldName)),
  );
  if (superseded.size === 0) return items;

  return items.filter(item => {
    if (item.status === 'edited') return true;
    return !superseded.has(normalized(item.name))
      && !superseded.has(normalized(item.displayLabel));
  });
}

/** True when an historical result explicitly names an edited-away request. */
export function isSupersededResult(
  matchNames: string[],
  editedItems: Array<{ oldName?: string }>,
): boolean {
  const names = new Set(matchNames.map(normalized).filter(Boolean));
  return editedItems.some(item => item.oldName && names.has(normalized(item.oldName)));
}
